import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Eye, Filter } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useLoader } from '../../components/shared/LoaderContext';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'REFERRAL_PENDING', label: 'Referral Pending' },
  { value: 'REFERRAL_APPROVED', label: 'Referral Approved' },
  { value: 'PAYMENT_PENDING', label: 'Payment Pending' },
  { value: 'PAYMENT_RECEIVED', label: 'Payment Received' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'REJECTED', label: 'Rejected' },
];

const statusBadge = {
  PENDING: 'pending', REFERRAL_PENDING: 'pending',
  REFERRAL_APPROVED: 'approved', PAYMENT_PENDING: 'pending',
  PAYMENT_RECEIVED: 'payment', ACTIVE: 'active', REJECTED: 'rejected',
};

export default function AdminMembersPage() {
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useLoader();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 15;

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      const res = await api.get(`/admin/members?${params}`);
      setMembers(res.data.members);
      setTotal(res.data.total);
    } catch {
      toast.error('Failed to fetch members');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMembers(); }, [page, statusFilter]);

  const handleSearch = async () => {
    setPage(1);
    const interval = showLoader('Searching members...');
    await fetchMembers();
    hideLoader(interval);
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter') handleSearch(); };

  const handleStatusChange = (e) => { setStatusFilter(e.target.value); setPage(1); };

  const totalPages = Math.ceil(total / limit);

  return (
    <div>
      <div className="page-header">
        <h2>Member Management</h2>
        <p>View, manage and activate member applications</p>
      </div>

      <div className="card" style={{ padding: '16px 20px', marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 250, position: 'relative' }}>
            <Search size={15} style={{
              position: 'absolute', left: 12, top: '50%',
              transform: 'translateY(-50%)', color: 'var(--text-muted)'
            }} />
            <input className="form-input"
              placeholder="Search by name, email or Member ID..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyDown} style={{ paddingLeft: 36 }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Filter size={15} style={{ color: 'var(--text-muted)' }} />
            <select className="form-input" value={statusFilter}
              onChange={handleStatusChange} style={{ width: 200 }}>
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <button className="btn btn-primary btn-sm" onClick={handleSearch}>
            <Search size={14} /> Search
          </button>
          {(search || statusFilter) && (
            <button className="btn btn-outline btn-sm" onClick={() => {
              setSearch(''); setStatusFilter(''); setPage(1);
            }}>Clear</button>
          )}
        </div>
        {total > 0 && (
          <div style={{ marginTop: 10, fontSize: 13, color: 'var(--text-muted)' }}>
            Showing {members.length} of {total} members
          </div>
        )}
      </div>

      <div className="card">
        {loading ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : members.length === 0 ? (
          <div className="empty-state">
            <Search size={40} />
            <p>No members found</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Member ID</th>
                  <th>Full Name</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  <th>City</th>
                  <th>Registered On</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.id}>
                    <td>
                      <span style={{ fontWeight: 700, color: 'var(--primary)', fontFamily: 'monospace', fontSize: 13 }}>
                        {m.memberId}
                      </span>
                    </td>
                    <td style={{ fontWeight: 500 }}>
                      {m.user.firstName} {m.user.middleName || ''} {m.user.lastName}
                    </td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>{m.user.email}</td>
                    <td style={{ fontSize: 13 }}>{m.user.mobile}</td>
                    <td style={{ fontSize: 13 }}>{m.city || '—'}</td>
                    <td style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                      {new Date(m.createdAt).toLocaleDateString('en-IN')}
                    </td>
                    <td>
                      <span className={`badge badge-${statusBadge[m.status] || 'pending'}`} style={{ fontSize: 11 }}>
                        {m.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td>
                      <button className="btn btn-outline btn-sm"
                        onClick={() => navigate(`/admin/members/${m.id}`)}>
                        <Eye size={13} /> View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div style={{
            display: 'flex', justifyContent: 'center', gap: 8,
            padding: '16px', borderTop: '1px solid var(--border)'
          }}>
            <button className="btn btn-outline btn-sm"
              onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
              ← Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button key={p} className={`btn btn-sm ${p === page ? 'btn-primary' : 'btn-outline'}`}
                onClick={() => setPage(p)}>{p}</button>
            ))}
            <button className="btn btn-outline btn-sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
